import { Component, signal, inject, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '@auth0/auth0-angular';
import { Subscription } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { ToastModule } from 'primeng/toast';
import { NavMenuComponent, UserMenuComponent, FooterComponent } from '@formrails/shared/ui';
import { SessionTimeoutService } from '@formrails/shared/data-access';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, NavMenuComponent, UserMenuComponent, FooterComponent, ToastModule],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit, OnDestroy {
  protected readonly title = signal('formrails');
  private auth = inject(AuthService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  /** Name of the page on show, from the active route's `data.title`. */
  protected readonly pageTitle = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => {
        let route = this.route;
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route.snapshot.data['title'] ?? '';
      })
    ),
    { initialValue: '' }
  );

  private sessionTimeout = inject(SessionTimeoutService);
  private errorSubscription?: Subscription;
  private authSubscription?: Subscription;

  ngOnInit(): void {
    // Subscribe to Auth0 errors globally
    this.errorSubscription = this.auth.error$.subscribe(error => {
      if (error) {
        this.handleAuthError(error);
      }
    });

    // Starting without a session (e.g. a reload after it expired): clear any
    // locally stored work so it isn't reopened once the user signs back in.
    // isAuthenticated$ only emits after Auth0 has finished loading, so a
    // login callback in progress isn't mistaken for a signed-out start.
    this.authSubscription = this.auth.isAuthenticated$.pipe(
      take(1),
      filter(isAuthenticated => !isAuthenticated)
    ).subscribe(() => {
      this.sessionTimeout.clearLocalSessionData();
    });
  }

  ngOnDestroy(): void {
    this.errorSubscription?.unsubscribe();
    this.authSubscription?.unsubscribe();
  }

  private handleAuthError(error: any): void {
    console.error('Auth0 Error:', error);

    // The session has expired (silent token renewal failed). The Auth0 SDK
    // fails the request before it's sent, so there's no 401 — this is the
    // only signal. It does not redirect by itself.
    if (this.sessionTimeout.isSessionExpiredError(error)) {
      console.warn('Session expired - clearing local data and redirecting to login');
      this.sessionTimeout.handleSessionExpired();
    } else if (error.error === 'access_denied') {
      console.error('Access denied:', error.error_description);
      // Handle access denied
    } else {
      // Generic error handling
      console.error('Auth0 error details:', {
        error: error.error,
        errorDescription: error.error_description,
        errorUri: error.error_uri
      });
    }
  }

}
